// Constantes das tabelas HRN
// Baseado nas normas ABNT NBR 14153

export interface HRNTableValue {
  valor: number;
  /** Texto em português — continua sendo o que vai para o laudo e para o banco. */
  descricao: string;
  /** Último segmento da chave em `apreciacaoTabelas.hrn.<tabela>`. */
  chave: string;
}

/** Assinatura mínima do `t` do `useI18n()` — a constante não importa React. */
type Traduzir = (chave: string) => string;

const RAIZ = 'apreciacaoTabelas.hrn';

const CHAVE_TABELA: Record<'PO' | 'FE' | 'GS' | 'NP', string> = {
  PO: 'po',
  FE: 'fe',
  GS: 'gs',
  NP: 'np',
};

/**
 * Tabela 1: Parâmetros do grau de severidade de lesão (GS)
 */
export const TABELA_GS: HRNTableValue[] = [
  { valor: 0.1, descricao: 'Arranhão / contusão', chave: 'arranhaoContusao' },
  { valor: 0.5, descricao: 'Laceração / corte / leve problema de saúde', chave: 'laceracaoCorte' },
  { valor: 1, descricao: 'Quebra - ossos pequenos – dedos', chave: 'ossosPequenos' },
  { valor: 2, descricao: 'Quebra - ossos grandes – mão / braços / pernas', chave: 'ossosGrandes' },
  { valor: 4, descricao: 'Perda de 1 ou 2 dedos', chave: 'perdaDedos' },
  { valor: 8, descricao: 'Amputação de perna / braço, perda parcial da visão audição', chave: 'amputacaoParcial' },
  { valor: 10, descricao: 'Amputação de 2 pernas / braços, perda total da visão / audição em ambos os olhos / ouvidos', chave: 'amputacaoTotal' },
  { valor: 12, descricao: 'Doença grave ou permanente', chave: 'doencaGrave' },
  { valor: 15, descricao: 'Fatalidade', chave: 'fatalidade' },
];

/**
 * Tabela 2: Parâmetros da frequência de exposição (FE)
 */
export const TABELA_FE: HRNTableValue[] = [
  { valor: 0.5, descricao: 'Anualmente', chave: 'anualmente' },
  { valor: 1, descricao: 'Mensalmente', chave: 'mensalmente' },
  { valor: 1.5, descricao: 'Semanalmente', chave: 'semanalmente' },
  { valor: 2.5, descricao: 'Diariamente', chave: 'diariamente' },
  { valor: 4, descricao: 'Em termos de hora', chave: 'porHora' },
  { valor: 5, descricao: 'Constantemente', chave: 'constantemente' },
];

/**
 * Tabela 3: Parâmetros da probabilidade de ocorrência (PO)
 */
export const TABELA_PO: HRNTableValue[] = [
  { valor: 0.033, descricao: 'Quase impossível', chave: 'quaseImpossivel' },
  { valor: 1, descricao: 'Muito improvável', chave: 'muitoImprovavel' },
  { valor: 1.5, descricao: 'Improvável', chave: 'improvavel' },
  { valor: 2, descricao: 'Possível', chave: 'possivel' },
  { valor: 5, descricao: 'Plausível', chave: 'plausivel' },
  { valor: 8, descricao: 'Provável', chave: 'provavel' },
  { valor: 10, descricao: 'Muito provável', chave: 'muitoProvavel' },
  { valor: 15, descricao: 'Quase certeza', chave: 'quaseCerteza' },
];

/**
 * Tabela 4: Parâmetros do número de pessoas em risco (NP)
 */
export const TABELA_NP: HRNTableValue[] = [
  { valor: 1, descricao: '1 a 2 pessoas', chave: 'de1a2' },
  { valor: 2, descricao: '3 a 7 pessoas', chave: 'de3a7' },
  // 4, não 5: mantém a progressão 1-2-4-8-12 e alinha com backend/utils/hrn-tables.
  { valor: 4, descricao: '8 a 15 pessoas', chave: 'de8a15' },
  { valor: 8, descricao: '16 a 50 pessoas', chave: 'de16a50' },
  { valor: 12, descricao: 'Mais que 50 pessoas', chave: 'maisDe50' },
];

/**
 * Função para obter descrição de um valor HRN
 */
export function tabelaHRN(tipo: 'PO' | 'FE' | 'GS' | 'NP'): HRNTableValue[] {
  switch (tipo) {
    case 'PO':
      return TABELA_PO;
    case 'FE':
      return TABELA_FE;
    case 'GS':
      return TABELA_GS;
    case 'NP':
      return TABELA_NP;
  }
}

/** Chave de dicionário do item da tabela — `apreciacaoTabelas.hrn.gs.fatalidade`. */
export function chaveHRN(tipo: 'PO' | 'FE' | 'GS' | 'NP', chaveItem: string): string {
  return `${RAIZ}.${CHAVE_TABELA[tipo]}.${chaveItem}`;
}

/**
 * Mesma coisa que `obterDescricaoHRN`, mas no idioma da tela. Valor fora da
 * tabela devolve `undefined`, igual à versão em português.
 */
export function obterDescricaoHRNTraduzida(
  t: Traduzir,
  tipo: 'PO' | 'FE' | 'GS' | 'NP',
  valor: number
): string | undefined {
  const item = tabelaHRN(tipo).find((i) => Math.abs(i.valor - valor) < 0.001);
  return item ? t(chaveHRN(tipo, item.chave)) : undefined;
}

/** `formatarValorHRN` no idioma da tela: `15 - Fatality`. */
export function formatarValorHRNTraduzido(
  t: Traduzir,
  tipo: 'PO' | 'FE' | 'GS' | 'NP',
  valor: number
): string {
  const descricao = obterDescricaoHRNTraduzida(t, tipo, valor);
  return descricao ? `${valor} - ${descricao}` : valor.toString();
}

export function obterDescricaoHRN(
  tipo: 'PO' | 'FE' | 'GS' | 'NP',
  valor: number
): string | undefined {
  let tabela: HRNTableValue[];
  switch (tipo) {
    case 'PO':
      tabela = TABELA_PO;
      break;
    case 'FE':
      tabela = TABELA_FE;
      break;
    case 'GS':
      tabela = TABELA_GS;
      break;
    case 'NP':
      tabela = TABELA_NP;
      break;
  }
  return tabela.find(item => Math.abs(item.valor - valor) < 0.001)?.descricao;
}

/**
 * Função para formatar valor HRN com descrição
 */
export function formatarValorHRN(
  tipo: 'PO' | 'FE' | 'GS' | 'NP',
  valor: number
): string {
  const descricao = obterDescricaoHRN(tipo, valor);
  if (descricao) {
    return `${valor} - ${descricao}`;
  }
  return valor.toString();
}

